import { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import {
  GetRevenueTransaction,
  GetRevenueVoid,
  RevenueTransactionItem,
  RevenueVoidItem,
  RevenueMonitorResponse,
} from "../services/revenueservice";
import { fetchdropdownMall, fetchdropdownTenant } from "../services/dropdown";

export type RowItem = RevenueTransactionItem | RevenueVoidItem;

interface SelectOption {
  value: string;
  label: string;
}

export const STATUS_OPTIONS = [
  { value: "valid", label: "Valid" },
  { value: "void", label: "Void" },
];

const fmt = (d: Date): string => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export function useRevenueTable() {
  const [mallOptions, setMallOptions] = useState<SelectOption[]>([]);
  const [selectedMall, setSelectedMall] = useState("");
  const [tenantOptions, setTenantOptions] = useState<SelectOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("valid");
  const [searchQuery, setSearchQuery] = useState("");

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState<Date | null>(firstDayOfMonth);
  const [endDate, setEndDate] = useState<Date | null>(today);
  const [tableData, setTableData] = useState<RowItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetchdropdownMall(0);
        if (!res?.dropdownClassDatas) return;

        const options: SelectOption[] = res.dropdownClassDatas.map(
          (m: { DataId: number; DataLabel: string }) => ({
            value: m.DataId.toString(),
            label: m.DataLabel,
          })
        );
        setMallOptions(options);

        const stored =
          localStorage.getItem("mall_id") || sessionStorage.getItem("mall_id");
        if (stored && options.find((o) => o.value === stored)) {
          setSelectedMall(stored);
        } else if (options.length === 1) {
          setSelectedMall(options[0].value);
          localStorage.setItem("mall_id", options[0].value);
        }
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch mall options",
        });
      }
    };
    load();
  }, []);
  useEffect(() => {
    if (!selectedMall) {
      setTenantOptions([]);
      setSelectedTenant("");
      setTableData([]);
      return;
    }
    const load = async () => {
      try {
        const res = await fetchdropdownTenant(parseInt(selectedMall, 10));
        if (!res?.dropdownClassDatas) return;

        const options: SelectOption[] = res.dropdownClassDatas.map(
          (t: { DataId: number; DataLabel: string }) => ({
            value: t.DataId.toString(),
            label: t.DataLabel,
          })
        );
        setTenantOptions(options);

        if (options.length === 1) {
          setSelectedTenant(options[0].value);
          localStorage.setItem("tenant_id", options[0].value);
        } else {
          const stored =
            localStorage.getItem("tenant_id") ||
            sessionStorage.getItem("tenant_id");
          if (stored && options.find((o) => o.value === stored)) {
            setSelectedTenant(stored);
          } else {
            setSelectedTenant("");
            setTableData([]);
          }
        }
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch tenant options",
        });
      }
    };
    load();
  }, [selectedMall]);
  useEffect(() => {
    if (!selectedTenant || !startDate || !endDate) {
      setTableData([]);
      return;
    }
    const PAGE_SIZE = 1000;

    const fetchAllPages = async (
      fetcher: (
        tenantId: number,
        type: number,
        start: string,
        end: string,
        page: number,
        size: number
      ) => Promise<RevenueMonitorResponse | null>
    ): Promise<RowItem[]> => {
      const tenantId = parseInt(selectedTenant, 10);
      const start = fmt(startDate);
      const end = fmt(endDate);
      const first = await fetcher(tenantId, 1, start, end, 1, PAGE_SIZE);
      if (!first) return [];

      const firstData: RowItem[] =
        first.revenueDatas ?? first.Content?.revenueDatas ?? [];
      const totalRecord: number =
        first.TotalRecord ?? first.Content?.TotalRecord ?? firstData.length;
      const totalPages = Math.ceil(totalRecord / PAGE_SIZE);

      if (totalPages <= 1) return firstData;
      const pageNumbers = Array.from(
        { length: totalPages - 1 },
        (_, i) => i + 2
      );
      const rest = await Promise.all(
        pageNumbers.map((page) =>
          fetcher(tenantId, 1, start, end, page, PAGE_SIZE)
        )
      );

      const restData: RowItem[] = rest.flatMap(
        (r) => r?.revenueDatas ?? r?.Content?.revenueDatas ?? []
      );

      return [...firstData, ...restData];
    };

    const load = async () => {
      setIsLoading(true);
      setTableData([]);
      try {
        const fetcher =
          selectedStatus === "valid" ? GetRevenueTransaction : GetRevenueVoid;
        const allData = await fetchAllPages(fetcher);
        setTableData(allData);
      } catch {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to fetch transaction data",
        });
        setTableData([]);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [selectedTenant, selectedStatus, startDate, endDate]);
  const isMallDisabled = mallOptions.length === 1;
  const isTenantDisabled =
    !selectedMall || tenantOptions.length === 0 || tenantOptions.length === 1;
  const handleMallChange = (value: string) => {
    setSelectedMall(value);
    localStorage.setItem("mall_id", value);
    setSelectedTenant("");
    setTableData([]);
  };

  const handleTenantChange = (value: string) => {
    setSelectedTenant(value);
    localStorage.setItem("tenant_id", value);
  };

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handlePageChange = (page: number) => setCurrentPage(page);

  const handleRowsPerPageChange = (newPerPage: number, page: number) => {
    setRowsPerPage(newPerPage);
    setCurrentPage(page);
  };
  const filteredData = useMemo(
    () =>
      tableData.filter((row) =>
        Object.values(row).some((val) =>
          String(val).toLowerCase().includes(searchQuery.toLowerCase())
        )
      ),
    [tableData, searchQuery]
  );
  const totalAmount = useMemo(
    () => tableData.reduce((sum, r) => sum + r.Amount, 0),
    [tableData]
  );
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatAmount = (amount: number): string =>
    new Intl.NumberFormat("id-ID", { maximumFractionDigits: 0 }).format(amount);

  return {
    mallOptions,
    selectedMall,
    isMallDisabled,
    tenantOptions,
    selectedTenant,
    isTenantDisabled,
    selectedStatus,
    setSelectedStatus,
    startDate,
    endDate,
    searchQuery,
    setSearchQuery,
    handleMallChange,
    handleTenantChange,
    handleDateChange,
    handlePageChange,
    handleRowsPerPageChange,
    filteredData,
    isLoading,
    totalAmount,
    currentPage,
    rowsPerPage,
    formatDate,
    formatAmount,
  };
}